import * as Headless from '@headlessui/react';
import { LucideProps, Search } from 'lucide-react';
import type React from 'react';
import { RefAttributes, createElement, useState } from 'react';
import { ForwardRefExoticComponent } from 'react';

import { TextInput } from '@core/components/inputs/TextInput';
import { Link } from '@core/components/Link';
import { Text } from '@core/components/Text';
import { cn } from '@core/utils/styles';

const baseClasses = [
  'inline-flex items-center',
  'rounded-theme outline-offset-2',
  'font-medium',
  'data-focus:outline-2 data-focus:outline-primary dark:data-focus:outline-primary',
  'data-disabled:opacity-75 data-disabled:pointer-events-none',
];

const ghostClasses = [
  'bg-transparent',
  'text-primary',
  'underline',
  'data-hover:bg-back-nav-highlight-light/5',
  'dark:data-hover:bg-back-nav-highlight-dark',
  'dark:data-hover:bg-back-light/10',
  'dark:text-primary-300',
];

export interface DropdownOption {
  label: string;
  value?: string;
  href?: string;
  onClick?: () => void;
  icon?: ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>;
}

interface DropdownSearch {
  placeholder: string;
  emptyMessage: string;
}

interface DropdownProps extends Headless.MenuProps {
  options: DropdownOption[];
  children: React.ReactNode;
  search?: DropdownSearch;
  className?: string;
  buttonClassName?: string;
  onOptionClick?: (option: DropdownOption) => void;
}

export function Dropdown({
  options,
  className,
  buttonClassName,
  children,
  search,
  onOptionClick,
  ...props
}: DropdownProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Headless.Menu {...props}>
      <Headless.MenuButton
        aria-label="Open options dropdown"
        className={cn(baseClasses, ghostClasses, 'p-2 no-underline', buttonClassName)}
      >
        {children}
      </Headless.MenuButton>

      <Headless.MenuItems
        transition
        anchor="bottom"
        className={cn(
          'border-stroke-light dark:border-stroke-dark rounded-theme-max min-w-40 origin-top-right border bg-white/5 p-1 text-sm/6 text-white shadow backdrop-blur-xl transition duration-100 ease-out',
          '[--anchor-gap:theme(spacing.2)] [--anchor-padding:theme(spacing.1)] focus:outline-none data-[anchor~=end]:[--anchor-offset:6px] data-[anchor~=start]:[--anchor-offset:-6px] sm:data-[anchor~=end]:[--anchor-offset:4px] sm:data-[anchor~=start]:[--anchor-offset:-4px]',
          className
        )}
      >
        {search && (
          <>
            <div className="col-span-full m-1 mb-2">
              <TextInput
                name="filters-search"
                type="search"
                placeholder={search.placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startIcon={<Search className="size-4" />}
              />
            </div>

            {filteredOptions.length === 0 && (
              <div className="col-span-full m-1 mt-0 flex items-center justify-center">
                <Text size="sm" variant="muted">
                  {search.emptyMessage}
                </Text>
              </div>
            )}
          </>
        )}

        {filteredOptions.map((option) => (
          <Headless.MenuItem key={option.label}>
            {createElement(
              option.href ? Link : 'button',
              {
                href: option.href as string,
                className:
                  'group flex w-full items-center gap-2 rounded-theme px-3 py-1.5 data-focus:bg-back-dark/5 dark:data-focus:bg-back-light/10',
                onClick: () => {
                  option.onClick?.();
                  onOptionClick?.(option);
                },
              },
              <>
                {option.icon && (
                  <option.icon className="text-icon-light dark:text-icon-dark mr-2 size-4" />
                )}
                <Text size="sm">{option.label}</Text>
              </>
            )}
          </Headless.MenuItem>
        ))}
      </Headless.MenuItems>
    </Headless.Menu>
  );
}
